import React, { useState, useCallback, useMemo } from 'react';
import './MobileNewCampaignModal.css';

// Hooks
import useCampaign from '../../../hooks/useCampaign';
import useCompany from '../../../hooks/useCompany';
import useLanguage from '../../../hooks/useLanguage';
import { useMobileAlert } from '../common/alerts/MobileAlertContext';
import { hasGeneratedDna } from '../../../utils/companyDna';

// Icons
import iconTarget from '../../../assets/images/icons/icon-target.svg';
import iconDescription from '../../../assets/images/icons/icon-description.svg';
import iconFrequency from '../../../assets/images/icons/icon-frequency.svg';
import iconCalendar from '../../../assets/images/icons/icon-calendar.svg';
import iconSocial from '../../../assets/images/icons/icon-social.svg';
import iconTone from '../../../assets/images/icons/icon-tone.svg';
import iconInstagram from '../../../assets/images/icons/icon-instagram.svg';
import iconLinkedin from '../../../assets/images/icons/icon-linkedin.svg';
import iconPdf from '../../../assets/images/icons/icon-pdf.svg';
import iconImage from '../../../assets/images/icons/icon-image.svg';

const TONE_OPTIONS = [
  { value: 'Professional', translationKey: 'professional' },
  { value: 'Friendly', translationKey: 'friendly' },
  { value: 'Casual', translationKey: 'casual' },
  { value: 'Formal', translationKey: 'formal' },
  { value: 'Enthusiastic', translationKey: 'enthusiastic' }
];

const OBJECTIVE_OPTIONS = [
  { value: 'Awareness', apiValue: 'brand_awareness', translationKey: 'awareness' },
  { value: 'Conversion', apiValue: 'lead_generation', translationKey: 'conversion' },
  { value: 'Engagement', apiValue: 'engagement', translationKey: 'engagement' },
  { value: 'Lead Generation', apiValue: 'lead_generation', translationKey: 'leadGeneration' },
  { value: 'Brand Building', apiValue: 'brand_awareness', translationKey: 'brandBuilding' }
];

const TARGET_AUDIENCE_DEFINITIONS = [
  {
    id: 'young_professionals',
    data: {
      description: 'Young professionals aged 25-35',
      demographics: 'Urban, tech-savvy, disposable income $50k+',
      interests: ['technology', 'career', 'innovation']
    }
  },
  {
    id: 'entrepreneurs',
    data: {
      description: 'Entrepreneurs and small business owners',
      demographics: 'Business owners, decision makers, 30-50 years old',
      interests: ['business', 'entrepreneurship', 'growth']
    }
  },
  {
    id: 'students',
    data: {
      description: 'Students and young adults aged 18-24',
      demographics: 'College students, entry-level professionals',
      interests: ['education', 'social media', 'trends']
    }
  },
  {
    id: 'families',
    data: {
      description: 'Parents and families with children',
      demographics: 'Parents aged 30-45, middle to upper income',
      interests: ['family', 'parenting', 'lifestyle']
    }
  },
  {
    id: 'seniors',
    data: {
      description: 'Senior adults aged 55 and above',
      demographics: 'Retirees, established professionals, 55+ years old',
      interests: ['health', 'travel', 'hobbies']
    }
  },
  {
    id: 'tech_enthusiasts',
    data: {
      description: 'Technology enthusiasts and early adopters',
      demographics: 'Tech-savvy individuals, 20-40 years old',
      interests: ['technology', 'gadgets', 'AI', 'innovation']
    }
  }
];

const MobileNewCampaignModal = ({
  onClose,
  onCampaignCreated
}) => {
  // Hooks
  const {
    createCampaign,
    loading: campaignLoading,
    error: campaignError,
    clearError: clearCampaignError
  } = useCampaign();

  const { currentCompany } = useCompany();
  const { t, language } = useLanguage();
  const { notify } = useMobileAlert();

  // Form state
  const [campaignName, setCampaignName] = useState('');
  const [description, setDescription] = useState('');
  const [frequency, setFrequency] = useState(3);
  const [startDate, setStartDate] = useState('');
  const [socialChannels, setSocialChannels] = useState({
    instagram: false,
    linkedin: false
  });
  const [selectedTone, setSelectedTone] = useState('');
  const [selectedObjective, setSelectedObjective] = useState('');
  const [selectedTargetAudience, setSelectedTargetAudience] = useState('');
  const [mediaOptions, setMediaOptions] = useState({
    pdf: false,
    image: false
  });
  const [uploadedFiles, setUploadedFiles] = useState({
    pdf: [],
    image: []
  });

  // Validation state - traccia i campi toccati dall'utente
  const [touchedFields, setTouchedFields] = useState({
    campaignName: false,
    description: false,
    selectedObjective: false,
    startDate: false,
    socialChannels: false,
    selectedTone: false,
    selectedTargetAudience: false
  });

  // Dropdown states
  const [showFrequencyDropdown, setShowFrequencyDropdown] = useState(false);
  const [showToneDropdown, setShowToneDropdown] = useState(false);
  const [showObjectiveDropdown, setShowObjectiveDropdown] = useState(false);
  const [showTargetAudienceDropdown, setShowTargetAudienceDropdown] = useState(false);

  // Date picker states
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [pickerMonth, setPickerMonth] = useState(new Date());

  // Loading state
  const [isLoading, setIsLoading] = useState(false);
  const isProcessing = isLoading || campaignLoading;

  // Loading messages
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);

  const resolvedLocale = useMemo(() => {
    if (!language) {
      return 'it-IT';
    }

    if (language.startsWith('it')) {
      return 'it-IT';
    }

    if (language.startsWith('en')) {
      return 'en-US';
    }

    return language;
  }, [language]);

  const loadingMessages = useMemo(() => {
    const messages = t('newCampaign.loading.messages', { returnObjects: true });
    if (Array.isArray(messages) && messages.length) {
      return messages;
    }
    return [t('common.loading')];
  }, [t, language]);

  const toneOptions = useMemo(() => {
    return TONE_OPTIONS.map((option) => ({
      ...option,
      label: t(`newCampaign.fields.tone.options.${option.translationKey}`)
    }));
  }, [t, language]);

  const objectiveOptions = useMemo(() => {
    return OBJECTIVE_OPTIONS.map((option) => ({
      ...option,
      label: t(`newCampaign.fields.objective.options.${option.translationKey}`)
    }));
  }, [t, language]);

  const targetAudienceOptions = useMemo(() => {
    return TARGET_AUDIENCE_DEFINITIONS.map((option) => ({
      ...option,
      label: t(`newCampaign.fields.targetAudience.options.${option.id}`)
    }));
  }, [t, language]);

  const weekdayLabels = useMemo(() => {
    const labels = t('newCampaign.calendar.weekdaysShort', { returnObjects: true });
    if (Array.isArray(labels) && labels.length === 7) {
      return labels;
    }

    return language?.startsWith('it')
      ? ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom']
      : ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  }, [t, language]);

  const monthFormatter = useMemo(() => {
    try {
      return new Intl.DateTimeFormat(resolvedLocale, { month: 'long', year: 'numeric' });
    } catch (error) {
      console.warn('[MobileNewCampaignModal] Falling back to default month formatter', error);
      return new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' });
    }
  }, [resolvedLocale]);

  const startDateFormatter = useMemo(() => {
    try {
      return new Intl.DateTimeFormat(resolvedLocale, {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    } catch (error) {
      console.warn('[MobileNewCampaignModal] Falling back to default date formatter', error);
      return new Intl.DateTimeFormat('en-US', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    }
  }, [resolvedLocale]);

  const displayStartDate = useMemo(() => {
    if (!startDate) {
      return '';
    }

    const parsedDate = new Date(startDate);
    if (Number.isNaN(parsedDate.getTime())) {
      return '';
    }

    return startDateFormatter.format(parsedDate);
  }, [startDate, startDateFormatter]);

  const selectedObjectiveLabel = useMemo(() => {
    const match = objectiveOptions.find((option) => option.value === selectedObjective);
    return match?.label || '';
  }, [objectiveOptions, selectedObjective]);

  const selectedToneLabel = useMemo(() => {
    const match = toneOptions.find((option) => option.value === selectedTone);
    return match?.label || '';
  }, [toneOptions, selectedTone]);

  const selectedTargetAudienceLabel = useMemo(() => {
    const match = targetAudienceOptions.find((option) => option.id === selectedTargetAudience);
    return match?.label || '';
  }, [targetAudienceOptions, selectedTargetAudience]);

  const currentLoadingMessage = loadingMessages.length
    ? loadingMessages[currentMessageIndex % loadingMessages.length]
    : '';

  // Validazione form - calcola se il form è valido
  const isFormValid = useMemo(() => {
    // Verifica che il nome campagna sia compilato
    if (!campaignName.trim()) return false;

    // Verifica che la descrizione sia compilata
    if (!description.trim()) return false;

    // Verifica che l'obiettivo sia selezionato
    if (!selectedObjective) return false;

    // Verifica che il tono sia selezionato
    if (!selectedTone) return false;

    // Verifica che il target audience sia selezionato
    if (!selectedTargetAudience) return false;

    // Verifica che la data di inizio sia compilata
    if (!startDate) return false;

    // Verifica che almeno un canale social sia selezionato
    const hasSelectedChannel = Object.values(socialChannels).some(channel => channel);
    if (!hasSelectedChannel) return false;

    return true;
  }, [campaignName, description, selectedObjective, selectedTone, selectedTargetAudience, startDate, socialChannels]);

  // Funzione helper per marcare un campo come "toccato"
  const markFieldAsTouched = (fieldName) => {
    setTouchedFields(prev => ({
      ...prev,
      [fieldName]: true
    }));
  };

  // Rotate loading messages
  React.useEffect(() => {
    if (isProcessing && loadingMessages.length > 1) {
      const interval = setInterval(() => {
        setCurrentMessageIndex((prev) => (prev + 1) % loadingMessages.length);
      }, 9000);
      return () => clearInterval(interval);
    }

    setCurrentMessageIndex(0);
    return undefined;
  }, [isProcessing, loadingMessages.length]);

  // Helper functions
  const toggleSocialChannel = (channel) => {
    setSocialChannels({
      ...socialChannels,
      [channel]: !socialChannels[channel]
    });
    markFieldAsTouched('socialChannels');
  };

  const handleFileUpload = (fileType) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    
    if (fileType === 'pdf') {
      input.accept = '.pdf';
    } else if (fileType === 'image') {
      input.accept = 'image/*';
    }
    
    input.onchange = (e) => {
      const files = Array.from(e.target.files);
      setUploadedFiles(prev => ({
        ...prev,
        [fileType]: [...prev[fileType], ...files]
      }));
      
      setMediaOptions(prev => ({
        ...prev,
        [fileType]: true
      }));
    };
    
    input.click();
  };

  const resetForm = () => {
    setCampaignName('');
    setDescription('');
    setFrequency(3);
    setStartDate('');
    setSocialChannels({ instagram: false, linkedin: false });
    setSelectedTone('');
    setSelectedObjective('');
    setSelectedTargetAudience('');
    setMediaOptions({ pdf: false, image: false });
    setUploadedFiles({ pdf: [], image: [] });
    setTouchedFields({
      campaignName: false,
      description: false,
      selectedObjective: false,
      startDate: false,
      socialChannels: false,
      selectedTone: false,
      selectedTargetAudience: false
    });
    setShowFrequencyDropdown(false);
    setShowToneDropdown(false);
    setShowObjectiveDropdown(false);
    setShowTargetAudienceDropdown(false);
    setShowDatePicker(false);
  };

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [onClose]);

  // Date picker helpers
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    // Add empty slots for days before the first day of the month
    for (let i = 0; i < (startingDayOfWeek === 0 ? 6 : startingDayOfWeek - 1); i++) {
      days.push(null);
    }
    // Add all days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }
    return days;
  };

  const navigatePickerMonth = (direction) => {
    const newMonth = new Date(pickerMonth);
    newMonth.setMonth(newMonth.getMonth() + direction);
    setPickerMonth(newMonth);
  };

  const handleDateSelect = (date) => {
    if (!date) return;

    // Format date as YYYY-MM-DD for input value
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const formattedDate = `${year}-${month}-${day}`;

    setStartDate(formattedDate);
    setShowDatePicker(false);
    markFieldAsTouched('startDate');
  };

  const isToday = (date) => {
    if (!date) return false;
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const openDatePicker = () => {
    setPickerMonth(startDate ? new Date(startDate) : new Date());
    setShowDatePicker(true);
  };

  const handleCreateCampaign = async () => {
    console.log('[MobileNewCampaignModal] Starting campaign creation');
    
    // Clear any previous errors
    clearCampaignError();
    
    // Collect selected social channels
    const socialChannelsArray = Object.keys(socialChannels).filter(
      channel => socialChannels[channel]
    );
    
    // Ensure we have a company to associate with the campaign
    if (!currentCompany) {
      console.error('[MobileNewCampaignModal] No company selected for campaign creation');
      notify({
        variant: 'warning',
        title: t('newCampaign.alerts.noCompany')
      });
      return;
    }

    if (!hasGeneratedDna(currentCompany)) {
      notify({
        variant: 'warning',
        title: t('newCampaign.alerts.dnaRequired', {
          defaultValue: 'Prima di creare una strategia crea il tuo DNA.'
        })
      });
      return;
    }
    
    // Set loading state
    setIsLoading(true);
    
    try {
      // Check if we have files to upload
      const hasFiles = uploadedFiles.pdf.length > 0 || uploadedFiles.image.length > 0;
      const targetAudienceEntry = selectedTargetAudience
        ? targetAudienceOptions.find(opt => opt.id === selectedTargetAudience)
        : null;

      const targetAudienceData = targetAudienceEntry?.data || {
        description: 'General audience',
        demographics: 'All demographics',
        interests: ['general']
      };

      const objectiveApiValue = OBJECTIVE_OPTIONS.find((option) => option.value === selectedObjective)?.apiValue || 'brand_awareness';
      const campaignTone = selectedTone || 'Professional';
      const campaignNameValue = campaignName || t('newCampaign.defaults.campaignName');

      const campaignPayload = {
        company_id: currentCompany.id,
        name: campaignNameValue,
        description: description || '',
        objective: objectiveApiValue,
        target_platforms: socialChannelsArray.length > 0 ? socialChannelsArray.map((platform) => platform.toLowerCase()) : ['linkedin'],
        posting_frequency: frequency,
        communication_tone: campaignTone,
        start_date: startDate ? `${startDate}T00:00:00.000Z` : new Date().toISOString(),
        target_audience: targetAudienceData,
        budget: 1000.0
      };

      if (hasFiles) {
        console.log('[MobileNewCampaignModal] Files detected, creating campaign with file upload');

        const formData = new FormData();
        formData.append('campaign_data', JSON.stringify(campaignPayload));

        uploadedFiles.pdf.forEach((file) => {
          formData.append('pdf_files', file);
        });

        uploadedFiles.image.forEach((file) => {
          formData.append('image_files', file);
        });

        console.log('[MobileNewCampaignModal] Creating campaign with files');
        const newCampaign = await createCampaign(formData, { isFormData: true });

        console.log('[MobileNewCampaignModal] Campaign with files created successfully:', newCampaign);

        resetForm();

        if (onCampaignCreated) {
          onCampaignCreated(newCampaign);
        }
      } else {
        console.log('[MobileNewCampaignModal] No files detected, creating campaign without files');

        console.log('[MobileNewCampaignModal] Creating campaign with data:', campaignPayload);
        const newCampaign = await createCampaign(campaignPayload);

        console.log('[MobileNewCampaignModal] Campaign created successfully:', newCampaign);

        resetForm();

        if (onCampaignCreated) {
          onCampaignCreated(newCampaign);
        }
      }

    } catch (error) {
      console.error('[MobileNewCampaignModal] Campaign creation failed:', error);
      const fallbackMessage = error?.message || t('newCampaign.alerts.unknownError');
      notify({
        variant: 'error',
        title: t('newCampaign.alerts.creationFailed', { error: fallbackMessage })
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="mobile-new-campaign-page">
      {/* Header */}
      <div className="mobile-new-campaign-header">
        <button
          type="button"
          className="mobile-back-button"
          onClick={handleClose}
          aria-label={t('newCampaign.header.backAria')}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <h3>{t('newCampaign.header.title')}</h3>
        <div className="mobile-header-spacer"></div>
      </div>

      {/* Content */}
      <div className="mobile-new-campaign-content">
          {/* Campaign Name */}
          <div className="mobile-form-field">
            <div className={`mobile-field-input ${touchedFields.campaignName && !campaignName.trim() ? 'invalid' : ''}`}>
              <div className="mobile-field-icon">
                <img src={iconTarget} alt={t('newCampaign.accessibility.alt.campaign')} />
              </div>
              <input
                type="text"
                className="mobile-input-text"
                placeholder={t('newCampaign.fields.name.placeholder')}
                value={campaignName}
                onChange={(e) => setCampaignName(e.target.value)}
                onBlur={() => markFieldAsTouched('campaignName')}
              />
            </div>
            {touchedFields.campaignName && !campaignName.trim() && (
              <div className="mobile-field-error">{t('newCampaign.fields.name.error')}</div>
            )}
          </div>

          {/* Description */}
          <div className="mobile-form-field">
            <div className={`mobile-field-input ${touchedFields.description && !description.trim() ? 'invalid' : ''}`}>
              <div className="mobile-field-icon">
                <img src={iconDescription} alt={t('newCampaign.accessibility.alt.description')} />
              </div>
              <textarea
                className="mobile-input-textarea"
                placeholder={t('newCampaign.fields.description.placeholder')}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                onBlur={() => markFieldAsTouched('description')}
                rows={3}
              />
            </div>
            {touchedFields.description && !description.trim() && (
              <div className="mobile-field-error">{t('newCampaign.fields.description.error')}</div>
            )}
          </div>

          {/* Campaign Objective */}
          <div className="mobile-form-field">
            <div className={`mobile-field-input ${touchedFields.selectedObjective && !selectedObjective ? 'invalid' : ''}`}>
              <div className="mobile-dropdown-container">
                <div className="mobile-dropdown-expanded">
                  <div
                    className="mobile-dropdown-header"
                    onClick={() => {
                      setShowObjectiveDropdown(!showObjectiveDropdown);
                      setShowFrequencyDropdown(false);
                      setShowToneDropdown(false);
                      markFieldAsTouched('selectedObjective');
                    }}
                  >
                    <div className="mobile-dropdown-icon">
                      <img src={iconTarget} alt={t('newCampaign.accessibility.alt.objective')} />
                    </div>
                    <div className="mobile-dropdown-header-content">
                      <div className="mobile-select-label">{t('newCampaign.fields.objective.label')}</div>
                      <div className="mobile-select-value">
                        <span>{selectedObjectiveLabel || t('newCampaign.fields.objective.placeholder')}</span>
                        <svg className={`mobile-arrow-icon ${showObjectiveDropdown ? 'rotated' : ''}`} width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                    </div>
                  </div>

                  <div className={`mobile-dropdown-options ${showObjectiveDropdown ? 'open' : ''}`}>
                    {objectiveOptions.map((objective) => (
                      <div
                        key={objective.value}
                        className={`mobile-dropdown-item ${selectedObjective === objective.value ? 'active' : ''}`}
                        onClick={() => {
                          setSelectedObjective(objective.value);
                          setShowObjectiveDropdown(false);
                        }}
                      >
                        {objective.label}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            {touchedFields.selectedObjective && !selectedObjective && (
              <div className="mobile-field-error">{t('newCampaign.fields.objective.error')}</div>
            )}
          </div>

          {/* Communication Tone */}
          <div className="mobile-form-field">
            <div className={`mobile-field-input ${touchedFields.selectedTone && !selectedTone ? 'invalid' : ''}`}>
              <div className="mobile-dropdown-container">
                <div className="mobile-dropdown-expanded">
                  <div
                    className="mobile-dropdown-header"
                    onClick={() => {
                      setShowToneDropdown(!showToneDropdown);
                      setShowObjectiveDropdown(false);
                      setShowFrequencyDropdown(false);
                      markFieldAsTouched('selectedTone');
                    }}
                  >
                    <div className="mobile-dropdown-icon">
                      <img src={iconTone} alt={t('newCampaign.accessibility.alt.tone')} />
                    </div>
                    <div className="mobile-dropdown-header-content">
                      <div className="mobile-select-label">{t('newCampaign.fields.tone.label')}</div>
                      <div className="mobile-select-value">
                        <span>{selectedToneLabel || t('newCampaign.fields.tone.placeholder')}</span>
                        <svg className={`mobile-arrow-icon ${showToneDropdown ? 'rotated' : ''}`} width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                    </div>
                  </div>

                  <div className={`mobile-dropdown-options ${showToneDropdown ? 'open' : ''}`}>
                    {toneOptions.map((tone) => (
                      <div
                        key={tone.value}
                        className={`mobile-dropdown-item ${selectedTone === tone.value ? 'active' : ''}`}
                        onClick={() => {
                          setSelectedTone(tone.value);
                          setShowToneDropdown(false);
                        }}
                      >
                        {tone.label}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            {touchedFields.selectedTone && !selectedTone && (
              <div className="mobile-field-error">{t('newCampaign.fields.tone.error')}</div>
            )}
          </div>

          {/* Target Audience */}
          <div className="mobile-form-field">
            <div className={`mobile-field-input ${touchedFields.selectedTargetAudience && !selectedTargetAudience ? 'invalid' : ''}`}>
              <div className="mobile-dropdown-container">
                <div className="mobile-dropdown-expanded">
                  <div
                    className="mobile-dropdown-header"
                    onClick={() => {
                      setShowTargetAudienceDropdown(!showTargetAudienceDropdown);
                      setShowObjectiveDropdown(false);
                      setShowFrequencyDropdown(false);
                      setShowToneDropdown(false);
                      markFieldAsTouched('selectedTargetAudience');
                    }}
                  >
                    <div className="mobile-dropdown-icon">
                      <img src={iconTarget} alt={t('newCampaign.accessibility.alt.targetAudience')} />
                    </div>
                    <div className="mobile-dropdown-header-content">
                      <div className="mobile-select-label">{t('newCampaign.fields.targetAudience.label')}</div>
                      <div className="mobile-select-value">
                        <span>
                          {selectedTargetAudienceLabel || t('newCampaign.fields.targetAudience.placeholder')}
                        </span>
                        <svg className={`mobile-arrow-icon ${showTargetAudienceDropdown ? 'rotated' : ''}`} width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                    </div>
                  </div>

                  <div className={`mobile-dropdown-options ${showTargetAudienceDropdown ? 'open' : ''}`}>
                    {targetAudienceOptions.map((audience) => (
                      <div
                        key={audience.id}
                        className={`mobile-dropdown-item ${selectedTargetAudience === audience.id ? 'active' : ''}`}
                        onClick={() => {
                          setSelectedTargetAudience(audience.id);
                          setShowTargetAudienceDropdown(false);
                        }}
                      >
                        {audience.label}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            {touchedFields.selectedTargetAudience && !selectedTargetAudience && (
              <div className="mobile-field-error">{t('newCampaign.fields.targetAudience.error')}</div>
            )}
          </div>

          {/* Frequency */}
          <div className="mobile-form-field">
            <div className="mobile-field-input">
              <div className="mobile-dropdown-container">
                <div className="mobile-dropdown-expanded">
                  <div
                    className="mobile-dropdown-header"
                    onClick={() => {
                      setShowFrequencyDropdown(!showFrequencyDropdown);
                      setShowObjectiveDropdown(false);
                      setShowToneDropdown(false);
                    }}
                  >
                    <div className="mobile-dropdown-icon">
                      <img src={iconFrequency} alt={t('newCampaign.accessibility.alt.frequency')} />
                    </div>
                    <div className="mobile-dropdown-header-content">
                      <div className="mobile-select-label">{t('newCampaign.fields.frequency.label')}</div>
                      <div className="mobile-select-value">
                        <span>{t('newCampaign.fields.frequency.option', { count: frequency })}</span>
                        <svg className={`mobile-arrow-icon ${showFrequencyDropdown ? 'rotated' : ''}`} width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                    </div>
                  </div>

                  <div className={`mobile-dropdown-options ${showFrequencyDropdown ? 'open' : ''}`}>
                    {[1, 2, 3, 4, 5, 6, 7].map((value) => (
                      <div
                        key={value}
                        className={`mobile-dropdown-item ${frequency === value ? 'active' : ''}`}
                        onClick={() => {
                          setFrequency(value);
                          setShowFrequencyDropdown(false);
                        }}
                      >
                        {t('newCampaign.fields.frequency.option', { count: value })}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Start Date */}
          <div className="mobile-form-field">
            <div
              className={`mobile-field-input ${touchedFields.startDate && !startDate ? 'invalid' : ''}`}
              onClick={openDatePicker}
              style={{ cursor: 'pointer' }}
            >
              <div className="mobile-field-icon">
                <img src={iconCalendar} alt={t('newCampaign.accessibility.alt.calendar')} />
              </div>
              <div className="mobile-period-container">
                <div className="mobile-select-label">{t('newCampaign.fields.startDate.label')}</div>
                <div className="mobile-select-value">
                  <span>
                    {displayStartDate || t('newCampaign.fields.startDate.placeholder')}
                  </span>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginLeft: '6px', opacity: 0.6 }}>
                    <polyline points="6,9 12,15 18,9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </div>
            </div>
            {touchedFields.startDate && !startDate && (
              <div className="mobile-field-error">{t('newCampaign.fields.startDate.error')}</div>
            )}
          </div>

          {/* Social Channels */}
          <div className="mobile-form-field">
            <div className={`mobile-field-input mobile-social-channels-input ${touchedFields.socialChannels && !Object.values(socialChannels).some(channel => channel) ? 'invalid' : ''}`}>
              <div className="mobile-field-icon">
                <img src={iconSocial} alt={t('newCampaign.accessibility.alt.social')} />
              </div>
              <div className="mobile-social-channels-container">
                <div className="mobile-select-label">{t('newCampaign.fields.socialChannels.label')}</div>
                <div className="mobile-social-buttons">
                  <button
                    className={`mobile-social-button ${socialChannels.instagram ? 'active' : ''}`}
                    onClick={() => toggleSocialChannel('instagram')}
                    type="button"
                  >
                    <div className="mobile-social-icon-wrapper">
                      <img src={iconInstagram} alt={t('newCampaign.accessibility.alt.instagram')} className="mobile-social-icon" />
                    </div>
                    {t('newCampaign.fields.socialChannels.options.instagram')}
                  </button>
                  <button
                    className={`mobile-social-button ${socialChannels.linkedin ? 'active' : ''}`}
                    onClick={() => toggleSocialChannel('linkedin')}
                    type="button"
                  >
                    <div className="mobile-social-icon-wrapper">
                      <img src={iconLinkedin} alt={t('newCampaign.accessibility.alt.linkedin')} className="mobile-social-icon" />
                    </div>
                    {t('newCampaign.fields.socialChannels.options.linkedin')}
                  </button>
                </div>
              </div>
            </div>
            {touchedFields.socialChannels && !Object.values(socialChannels).some(channel => channel) && (
              <div className="mobile-field-error">{t('newCampaign.fields.socialChannels.error')}</div>
            )}
          </div>

          {/* File Upload Section */}
          <div className="mobile-upload-section">
            <div className="mobile-upload-header">
              <h4>{t('newCampaign.upload.title')}</h4>
            </div>
            
            <div className="mobile-upload-options">
              <div 
                className={`mobile-upload-option ${mediaOptions.pdf ? 'active' : ''}`}
                onClick={() => handleFileUpload('pdf')}
              >
                <div className="mobile-upload-icon-wrapper">
                  <img src={iconPdf} alt={t('newCampaign.accessibility.alt.pdf')} className="mobile-upload-icon" />
                </div>
                <div className="mobile-upload-content">
                  <h5>{t('newCampaign.upload.pdf.title')}</h5>
                  <p>{t('newCampaign.upload.pdf.description')}</p>
                </div>
                <div className="mobile-upload-action">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M12 5v14m-7-7h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </div>
              </div>
              
              <div 
                className={`mobile-upload-option ${mediaOptions.image ? 'active' : ''}`}
                onClick={() => handleFileUpload('image')}
              >
                <div className="mobile-upload-icon-wrapper">
                  <img src={iconImage} alt={t('newCampaign.accessibility.alt.image')} className="mobile-upload-icon" />
                </div>
                <div className="mobile-upload-content">
                  <h5>{t('newCampaign.upload.image.title')}</h5>
                  <p>{t('newCampaign.upload.image.description')}</p>
                </div>
                <div className="mobile-upload-action">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M12 5v14m-7-7h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>

      {/* Footer */}
      <div className="mobile-new-campaign-footer">
        <button
          className="mobile-cancel-button"
          onClick={handleClose}
          disabled={isProcessing}
        >
          {t('newCampaign.footer.cancel')}
        </button>
        <button
          className={`mobile-create-button ${isProcessing ? 'loading' : ''} ${!isFormValid && !isProcessing ? 'disabled' : ''}`}
          onClick={handleCreateCampaign}
          disabled={isProcessing || !isFormValid}
        >
          {isProcessing ? t('newCampaign.footer.creating') : t('newCampaign.footer.create')}
        </button>
      </div>

      {/* Date Picker Modal */}
      {showDatePicker && (
        <div className="date-picker-overlay" onClick={() => setShowDatePicker(false)}>
          <div className="date-picker-modal" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="date-picker-header">
              <button
                className="date-picker-nav-button"
                onClick={() => navigatePickerMonth(-1)}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>

              <div className="date-picker-month-title">
                {monthFormatter.format(pickerMonth)}
              </div>

              <button
                className="date-picker-nav-button"
                onClick={() => navigatePickerMonth(1)}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>

            {/* Weekday labels */}
            <div className="date-picker-weekdays">
              {weekdayLabels.map((day) => (
                <div key={day} className="date-picker-weekday">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar grid */}
            <div className="date-picker-grid">
              {getDaysInMonth(pickerMonth).map((date, index) => {
                const isTodayDate = date && isToday(date);
                const isSelected = date && startDate && new Date(startDate).toDateString() === date.toDateString();

                return (
                  <button
                    key={index}
                    className={`date-picker-day ${!date ? 'empty' : ''} ${isTodayDate ? 'today' : ''} ${isSelected ? 'selected' : ''}`}
                    onClick={() => handleDateSelect(date)}
                    disabled={!date}
                  >
                    {date ? date.getDate() : ''}
                  </button>
                );
              })}
            </div>

            {/* Quick actions */}
            <div className="date-picker-actions">
              <button
                className="date-picker-action-button"
                onClick={() => {
                  handleDateSelect(new Date());
                }}
              >
                {t('newCampaign.fields.startDate.today')}
              </button>
              <button
                className="date-picker-action-button secondary"
                onClick={() => setShowDatePicker(false)}
              >
                {t('newCampaign.fields.startDate.close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loading Overlay */}
      {isProcessing && (
        <div className="mobile-loading-overlay">
          <div className="mobile-loading-content">
            <div className="mobile-loading-spinner">
              <div className="mobile-spinner-ring"></div>
              <div className="mobile-spinner-ring"></div>
              <div className="mobile-spinner-ring"></div>
            </div>

            <h3 className="mobile-loading-title">{t('newCampaign.loading.title')}</h3>
            <p className="mobile-loading-subtitle">{t('newCampaign.loading.subtitle')}</p>

            <div className="mobile-loading-messages">
              <div className="mobile-loading-message" key={currentMessageIndex}>
                <span className="mobile-loading-icon">✨</span>
                <span className="mobile-loading-text">{currentLoadingMessage}</span>
                <span className="mobile-loading-dots">
                  <span className="dot">.</span>
                  <span className="dot">.</span>
                  <span className="dot">.</span>
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MobileNewCampaignModal;
